// Page script for /best-appointment-scheduling-apps-2026.
//
// Three behaviours the design annotations call for:
//   1. the sticky vendor rail tracks the scroll position (scroll spy), not just clicks
//   2. below 1024px the vendor cards collapse into an accordion, card 1 open
//   3. the comparison table shows a scroll-position indicator on mobile
//
// The industry tab set needs nothing here: it uses the shared tabs component,
// which the main bundle initialises eagerly from `data-lazy-init="tabs"`.

// Matches $tab in assets/scss/base/vars.scss.
const TABLET_QUERY = "(max-width: 1023px)";

/**
 * Highlights the rail link of whichever vendor card currently owns the viewport.
 * Uses IntersectionObserver rather than a scroll handler so the browser does the
 * bookkeeping, and falls back to click-only highlighting where it is unavailable.
 */
function initVendorRail() {
    const rail = document.querySelector("[data-vendor-rail]");
    if (!rail) {
        return;
    }

    const links = Array.from(rail.querySelectorAll(".best-apps-2026__rail-link"));
    const cards = Array.from(document.querySelectorAll("[data-vendor-card]"));
    if (!links.length || !cards.length) {
        return;
    }

    const linkByAnchor = new Map(
        links.map(link => [link.getAttribute("href").slice(1), link])
    );

    function setActive(anchor) {
        const target = linkByAnchor.get(anchor);
        if (!target || target.classList.contains("is-active")) {
            return;
        }
        links.forEach(link => {
            link.classList.remove("is-active");
            link.removeAttribute("aria-current");
        });
        target.classList.add("is-active");
        target.setAttribute("aria-current", "true");

        // Keep the active chip in view in the mobile chip row.
        if (rail.scrollWidth > rail.clientWidth) {
            const offset = target.offsetLeft - (rail.clientWidth - target.offsetWidth) / 2;
            rail.scrollTo({ left: Math.max(0, offset), behavior: "smooth" });
        }
    }

    // Clicking is authoritative straight away; the observer would otherwise only
    // catch up once the smooth scroll settles.
    links.forEach(link => {
        link.addEventListener("click", () => setActive(link.getAttribute("href").slice(1)));
    });

    if (typeof IntersectionObserver !== "function") {
        return;
    }

    const visible = new Set();

    const observer = new IntersectionObserver(
        entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    visible.add(entry.target);
                } else {
                    visible.delete(entry.target);
                }
            });

            if (!visible.size) {
                return;
            }

            // The card nearest the top of the detection band wins.
            const topmost = Array.from(visible).reduce((best, card) =>
                card.getBoundingClientRect().top < best.getBoundingClientRect().top ? card : best
            );
            setActive(topmost.id);
        },
        {
            // Detection band starts just under the fixed header and is deliberately
            // shallow, so the active item changes as a card's heading reaches the top
            // rather than when the card merely enters the viewport.
            rootMargin: "-130px 0px -65% 0px",
            threshold: 0,
        }
    );

    cards.forEach(card => observer.observe(card));
}

/**
 * Progressive disclosure for the vendor cards, mobile and tablet only. Desktop
 * keeps every card expanded, and with the script absent nothing collapses at all.
 */
function initVendorAccordion() {
    const cards = Array.from(document.querySelectorAll("[data-vendor-card]"));
    if (!cards.length || typeof window.matchMedia !== "function") {
        return;
    }

    const entries = cards.map(card => ({
        header: card.querySelector(".best-apps-2026__vendor-header"),
        toggle: card.querySelector(".best-apps-2026__vendor-toggle"),
        body: card.querySelector(".best-apps-2026__vendor-collapse"),
    })).filter(entry => entry.toggle && entry.body);

    if (!entries.length) {
        return;
    }

    const query = window.matchMedia(TABLET_QUERY);

    function expand(entry, open) {
        entry.toggle.setAttribute("aria-expanded", open ? "true" : "false");
        entry.body.hidden = !open;
    }

    function toggle(entry) {
        expand(entry, entry.toggle.getAttribute("aria-expanded") !== "true");
    }

    entries.forEach(entry => {
        entry.toggle.addEventListener("click", () => toggle(entry));

        // The whole header is the hit area wherever the cards collapse, matching
        // the FAQ accordion. The button keeps the keyboard and aria contract; it
        // handles its own clicks, so ignore the ones that land on it.
        if (entry.header) {
            entry.header.addEventListener("click", event => {
                if (!query.matches || entry.toggle.contains(event.target)) {
                    return;
                }

                toggle(entry);
            });
        }
    });

    function apply() {
        entries.forEach((entry, index) => {
            if (query.matches) {
                entry.toggle.hidden = false;
                expand(entry, index === 0);
            } else {
                entry.toggle.hidden = true;
                expand(entry, true);
            }
        });
    }

    apply();

    // addEventListener on MediaQueryList is unsupported in older Safari.
    if (typeof query.addEventListener === "function") {
        query.addEventListener("change", apply);
    } else if (typeof query.addListener === "function") {
        query.addListener(apply);
    }
}

/**
 * Mirrors the comparison table's horizontal scroll in the thin bar underneath it.
 * Purely an indicator: it is aria-hidden and is not draggable.
 */
function initCompareIndicator() {
    const scroller = document.querySelector("[data-compare-scroll]");
    const track = document.querySelector("[data-compare-indicator]");
    const thumb = document.querySelector("[data-compare-thumb]");
    if (!scroller || !track || !thumb) {
        return;
    }

    function update() {
        const scrollable = scroller.scrollWidth - scroller.clientWidth;
        if (scrollable <= 0) {
            track.style.visibility = "hidden";
            return;
        }
        track.style.visibility = "";

        const ratio = scroller.clientWidth / scroller.scrollWidth;
        const thumbWidth = Math.max(ratio * 100, 10);
        thumb.style.width = thumbWidth + "%";
        thumb.style.transform =
            "translateX(" + (scroller.scrollLeft / scrollable) * ((100 - thumbWidth) / thumbWidth) * 100 + "%)";
    }

    scroller.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    update();
}

function init() {
    initVendorRail();
    initVendorAccordion();
    initCompareIndicator();
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
} else {
    init();
}
